import React from 'react';

/**
 * ErrorBoundary
 * Catches ChunkLoadErrors (network drops) and render errors inside page components.
 * The shell (Sidebar, TopBar) stays interactive — only the page area shows the error card.
 * Resets automatically when the route changes (key={window.location.pathname} in App.js).
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('[ErrorBoundary] Caught error:', error, info);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/home';
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const isChunkError =
      this.state.error?.name === 'ChunkLoadError' ||
      this.state.error?.message?.includes('Loading chunk');

    return (
      <div style={styles.wrapper}>
        <div style={styles.card}>
          <div style={styles.iconWrap}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none"
              stroke="#e53e3e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>

          <h2 style={styles.title}>
            {isChunkError ? 'Connection problem' : 'Something went wrong'}
          </h2>

          <p style={styles.message}>
            {isChunkError
              ? 'A page chunk failed to load — this usually means a network blip or a new deployment. Reload to continue.'
              : 'An unexpected error occurred while rendering this page.'}
          </p>

          {!isChunkError && this.state.error && (
            <pre style={styles.detail}>
              {this.state.error.message}
            </pre>
          )}

          <div style={styles.actions}>
            <button style={styles.btnPrimary} onClick={this.handleReload}>
              Reload page
            </button>
            <button style={styles.btnSecondary} onClick={this.handleGoHome}>
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }
}

const styles = {
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    padding: '2rem',
  },
  card: {
    background: '#fff',
    border: '1px solid #fed7d7',
    borderRadius: '12px',
    padding: '2.5rem 2rem',
    maxWidth: '460px',
    width: '100%',
    textAlign: 'center',
    boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
  },
  iconWrap: {
    marginBottom: '1rem',
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#1a202c',
    marginBottom: '0.75rem',
  },
  message: {
    fontSize: '0.95rem',
    color: '#4a5568',
    lineHeight: 1.6,
    marginBottom: '1.25rem',
  },
  detail: {
    background: '#fff5f5',
    border: '1px solid #fed7d7',
    borderRadius: '6px',
    padding: '0.75rem 1rem',
    fontSize: '0.8rem',
    color: '#c53030',
    textAlign: 'left',
    overflowX: 'auto',
    marginBottom: '1.5rem',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  actions: {
    display: 'flex',
    gap: '0.75rem',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  btnPrimary: {
    padding: '0.55rem 1.4rem',
    background: '#3182ce',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.9rem',
    fontWeight: 500,
    cursor: 'pointer',
  },
  btnSecondary: {
    padding: '0.55rem 1.4rem',
    background: 'transparent',
    color: '#3182ce',
    border: '1px solid #3182ce',
    borderRadius: '6px',
    fontSize: '0.9rem',
    fontWeight: 500,
    cursor: 'pointer',
  },
};

export default ErrorBoundary;